








































import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { User } from '@/modules/backend/Entities/User';
import { Company } from '@/modules/backend/Entities/Company';
import { Building } from '@/modules/backend/Entities/Building';
import { Location } from '@/modules/backend/Entities/Location';
import { Account } from '@/modules/backend/Entities/Account';
import { Location as RouterLocation, Route } from 'vue-router';
import { resolveModule } from '@/store';
import { CompaniesModule } from '@/store/dashboard/companies/CompaniesModule';
import { BuildingsModule } from '@/store/dashboard/buildings/BuildingsModule';
import { LocationsModule } from '@/store/dashboard/locations/LocationsModule';
import { AccountsModule } from '@/store/dashboard/accounts/AccountsModule';

const AuthModule = namespace('app/auth');

enum TreeViewItemType {
  COMPANY = 'company',
  BUILDING = 'building',
  LOCATION = 'location',
  ACCOUNT = 'account',
}

type DashboardEntity = Company | Building | Location | Account;

interface TreeViewItem {
  key: string;
  id: number;
  name?: string;
  icon?: string;
  type: TreeViewItemType;
  origin?: DashboardEntity;
  children?: Array<TreeViewItem>;
}

interface ActiveTreeBranch {
  company: TreeViewItem | undefined;
  building: TreeViewItem | undefined;
  location: TreeViewItem | undefined;
  account: TreeViewItem | undefined;
}

@Component
export default class DrawerTreeView extends Vue {
  private readonly companiesModule: CompaniesModule = resolveModule(CompaniesModule);
  private readonly buildingsModule: BuildingsModule = resolveModule(BuildingsModule);
  private readonly locationsModule: LocationsModule = resolveModule(LocationsModule);
  private readonly accountsModule: AccountsModule = resolveModule(AccountsModule);

  private get BuildingsByCompanyId(): (companyId: number) => Building[] {
    return this.buildingsModule.BuildingsByCompanyId;
  }

  @AuthModule.Getter('Info')
  private readonly AuthInfo!: User;

  private items: TreeViewItem[] = [];
  private itemsOpen: string[] = [];
  private itemsActive: string[] = [];
  private search = '';

  @Watch('items', { immediate: true, deep: true })
  private async itemsChanged(): Promise<void> {
    const activeTreeBranch = await this.getActiveTreeBranch();

    /* Append every node from the active tree branch to the opened items array */
    this.itemsOpen = this.getArrayDistinct([
      ...this.itemsOpen,
      activeTreeBranch.company?.key || '',
      activeTreeBranch.building?.key || '',
      activeTreeBranch.location?.key || '',
      activeTreeBranch.account?.key || '',
    ].filter(item => item !== ''));

    /* Set the top-most node from the active tree branch as active */
    const activeItem = activeTreeBranch.account?.key ||
      activeTreeBranch.location?.key ||
      activeTreeBranch.building?.key ||
      activeTreeBranch.company?.key;
    if (activeItem) {
      this.itemsActive = [activeItem];
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getArrayDistinct(array: Array<any>): Array<any> {
    return Array.from(new Set(array));
  }

  private clearActiveItems(): void {
    this.itemsActive = [];
  }

  /**
   * Prevents click event propagation when clicking the active node in the TreeView.
   * Required to disable deselect of the active item
   *
   * @param event
   */
  private navigationTreeClickHandler(event: Event): void {
    const target = event.target;
    if (target) {
      /**
       *  TreeView node has multiple children each of them can be the target, so select the root node
       *  that is same for all the children
       */
      const parent: HTMLElement | null = (target as HTMLElement).closest('.v-treeview-node__root');

      /* Check if user is trying to click on the expand button to allow this event */
      const isToggleButton = (target as HTMLElement).classList.contains('v-treeview-node__toggle');

      /**
       *  If user clicked onto active item and not on the active element expand button at the same time
       *  then disable click event handling and do nothing
       */
      if (!isToggleButton && parent && parent.classList.contains('v-treeview-node--active')) {
        event.stopPropagation();
      }
    }
  }

  private activeItemChangedHandler(payload: string[]): void {
    const activeItem = payload[0] || undefined;

    if (activeItem) {
      const entity = this.getEntityFromKey(activeItem);
      if (entity) {
        this.$router.push(this.routerLinkItem(entity)).catch(() => {
          // Do nothing
        });
      }
    }
  }

  private getTreeViewItemFromEntity(entity: DashboardEntity, children: TreeViewItem[] | undefined = undefined): TreeViewItem {
    const result = {
      id: entity.getCompoundId(),
      name: entity.getName(),
      origin: entity,
      children: children || []
    };

    if (entity instanceof Company) {
      return {
        ...result,
        key: `${TreeViewItemType.COMPANY}-${entity.getId()}`,
        type: TreeViewItemType.COMPANY,
        icon: 'mdi-domain'
      };
    }

    if (entity instanceof Building) {
      return {
        ...result,
        key: `${TreeViewItemType.BUILDING}-${entity.getId()}`,
        type: TreeViewItemType.BUILDING,
        icon: 'mdi-office-building'
      };
    }

    if (entity instanceof Location) {
      return {
        ...result,
        key: `${TreeViewItemType.LOCATION}-${entity.getId()}`,
        type: TreeViewItemType.LOCATION,
        icon: entity.getIcon(),
      };
    }

    return {
      ...result,
      key: `${TreeViewItemType.ACCOUNT}-${entity.getId()}`,
      type: TreeViewItemType.ACCOUNT,
      icon: 'mdi-account',
      children: undefined,
    };
  }

  private getEntityFromKey(key: string): TreeViewItem | undefined {
    const [type, id] = key.split('-');
    if (!type || !id) {
      return undefined;
    }

    let entity: DashboardEntity | undefined;
    switch (type) {
      case TreeViewItemType.LOCATION:
        entity = this.locationsModule.LocationById(Number(id));
        break;
      case TreeViewItemType.BUILDING:
        entity = this.buildingsModule.BuildingById(Number(id));
        break;
      case TreeViewItemType.ACCOUNT:
        entity = this.accountsModule.AccountById(Number(id));
        break;
      case TreeViewItemType.COMPANY:
        entity = this.companiesModule.CompanyById(Number(id));
        break;
    }

    return entity ? this.getTreeViewItemFromEntity(entity) : undefined;
  }

  private async getEntityFromRoute(route: Route): Promise<DashboardEntity | undefined> {
    let entity: DashboardEntity | undefined;

    if (route.name?.indexOf('dashboard.company') === 0 && route.params.companyId) {
      entity = this.AuthInfo?.getCompanies().filter((company: Company) => {
        return company.getId() === Number(route.params.companyId);
      })[0];
    } else if (route.name?.indexOf('dashboard.building') === 0 && route.params.buildingId) {
      entity = await this.buildingsModule.FetchSingle(Number(route.params.buildingId));
    } else if (route.name?.indexOf('dashboard.location') === 0 && route.params.locationId) {
      entity = await this.locationsModule.FetchSingle(Number(route.params.locationId));
    } else if (route.name?.indexOf('dashboard.account') === 0 && route.params.accountId) {
      entity = await this.accountsModule.FetchSingle(Number(route.params.accountId));
    }

    return entity;
  }

  /**
   * Returns and object with currently active entity and its parents to the root node (Company entity)
   *
   * @return {Promise<{company: any; location: any; building: any; account: any} | {company: undefined; location: undefined; building: undefined; account: undefined}>}
   */
  private async getActiveTreeBranch(): Promise<ActiveTreeBranch> {
    const entity = await this.getEntityFromRoute(this.$route);
    if (!entity) {
      return {
        company: undefined,
        building: undefined,
        location: undefined,
        account: undefined
      };
    }

    let company, building, location, account;

    if (entity instanceof Company) {
      company = this.getTreeViewItemFromEntity(entity);
    }

    if (entity instanceof Building) {
      building = this.getTreeViewItemFromEntity(entity);
      company = this.getTreeViewItemFromEntity(entity.getCompany());
    }

    if (entity instanceof Location) {
      location = this.getTreeViewItemFromEntity(entity);
      const buildingEntity = entity.getBuilding();
      building = this.getTreeViewItemFromEntity(buildingEntity);
      company = this.getTreeViewItemFromEntity(buildingEntity.getCompany());
    }

    if (entity instanceof Account) {
      account = this.getTreeViewItemFromEntity(entity);
      const locationEntity = entity.getLocation();
      location = this.getTreeViewItemFromEntity(locationEntity);
      const buildingEntity = locationEntity.getBuilding();
      building = this.getTreeViewItemFromEntity(buildingEntity);
      company = this.getTreeViewItemFromEntity(buildingEntity.getCompany());
    }

    return { company, building, location, account };
  }

  private async loadChildren(item: TreeViewItem): Promise<void> {
    const originId: number | undefined = item.origin?.getId();

    let children: TreeViewItem[] = [];

    if (item.type === TreeViewItemType.COMPANY) {
      await this.buildingsModule.Fetch(item.id);
      children = this.BuildingsByCompanyId(item.id).map((building: Building) => {
        return this.getTreeViewItemFromEntity(building);
      });
    }

    if (originId && item.type === TreeViewItemType.BUILDING) {
      await this.locationsModule.Fetch(originId);
      children = this.locationsModule.LocationsByBuildingId(originId).map((location: Location) => {
        return this.getTreeViewItemFromEntity(location);
      });
    }

    if (originId && item.type === TreeViewItemType.LOCATION) {
      await this.accountsModule.Fetch(originId);
      children = this.accountsModule.AccountsByLocationId(originId).map((account: Account) => {
        return this.getTreeViewItemFromEntity(account);
      });
    }

    const availableKeys: string[] = item.children?.map(item => item.key) || [];
    children.forEach((child: TreeViewItem) => {
      if (availableKeys.indexOf(child.key) === -1 && Array.isArray(item.children)) {
        item.children.push(child);
      }
    });
  }

  private routerLinkItem(item: TreeViewItem): RouterLocation {
    const mapTypeToNextType = Object.freeze({
      company: 'buildings',
      building: 'locations',
      location: 'accounts',
      account: 'counters'
    });

    return {
      name: `dashboard.${item.type}.${mapTypeToNextType[item.type]}`,
      params: {
        [`${item.type}Id`]: String(item.origin?.getId())
      }
    };
  }

  public async mounted(): Promise<void> {
    this.$root.$on('treeview-clear-active', () => {
      this.clearActiveItems();
    });

    const availableCompanies = this.AuthInfo?.getCompanies();
    this.items = availableCompanies?.map(item => {
      this.companiesModule.FetchSingle(item.getId());
      return this.getTreeViewItemFromEntity(item);
    });
  }
}
